import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ClientSearch from "../../components/ClientInternalSearch";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import Search from "../../components/Search";
import { clientgetCustomerById, getAllCustomers } from "../../utils/api";
import useStore from "../../utils/store";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const Client = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const [clientData, setClientData] = useState({});
  const [data, setData] = useState({});
  const query = useQuery();
  const navigate = useNavigate();

  const getAllCustomerHandler = async (silent = false) => {
    if (!silent) setLoading(true);
    const [success, resData] = await getAllCustomers(startDate, endDate);
    if (success) {
      setCustomers(
        resData.map((item) => ({
          ...item,
          name: `${item.userName}${
            item?.group?.name !== undefined ? ` (${item?.group?.name})` : ""
          }`,
          curName: item?.name,
        }))
      );
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    getAllCustomerHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const getCustomersByIdHandler = async (id, name, silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    const [success, resData] = await clientgetCustomerById(id, startDate, endDate);

    if (success) {
      setCustomer({
        ...resData,
        name,
      });
    }
    if (!silent) setLoading(false);
  };

  const refreshAll = async (silent = true) => {
    const activeId = data?.cur || query?.get("id");
    if (!activeId) {
      await getAllCustomerHandler(silent);
      return;
    }
    const found = customers.find((item) => item._id === activeId);
    const activeName = found?.name || found?.userName || customer?.name;
    await Promise.all([
      getAllCustomerHandler(silent),
      getCustomersByIdHandler(activeId, activeName, silent),
    ]);
  };

  // Live Sync on Change: Only refreshes when an action or data change happens
  useEffect(() => {
    const handleDataUpdated = () => {
      refreshAll(true);
    };

    const handleStorage = (e) => {
      if (e.key === "plabel_last_sync") {
        refreshAll(true);
      }
    };

    window.addEventListener("plabel-data-updated", handleDataUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("plabel-data-updated", handleDataUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [data?.cur, startDate, endDate]);

  // Auto-restore / persist selected client from URL or LocalStorage
  useEffect(() => {
    const urlId = query?.get("id");
    const savedId = localStorage.getItem("selectedClientAdminId");
    const targetId = urlId || savedId;

    if (targetId && customers.length > 0) {
      const found = customers.find((item) => item._id === targetId);
      if (found) {
        setData({ cur: found._id });
        setClientData(found);
        getCustomersByIdHandler(found._id, found.name || found.userName);
        if (!urlId) {
          navigate(`/admin/client?id=${found._id}`, { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.get("id"), customers]);

  return (
    <Layout>
      <div className="flex gap-3 flex-col md:flex-row text-[80%]">
        {loading && <Loading />}
        <Search
          data={customers.filter((item) => item?.type === "Client")}
          item={(item) => {
            getCustomersByIdHandler(item._id, item?.name);
            setData({
              cur: item._id,
            });
            setClientData(item);
            localStorage.setItem("selectedClientAdminId", item._id);
            navigate(`/admin/client?id=${item._id}`, { replace: true });
          }}
          active={data.cur}
        />
        <div className="md:w-[calc(100%-16rem)]">
          {customer && (
            <ClientSearch
              id={data.cur}
              customers={customers}
              client={clientData}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Client;
